/**
 * Builds mock send results for a campaign: recipient rows first, then the report and grants
 * derived from those rows, so every number on the report reconciles with the recipient table.
 */
import type {
  AttributionMethod, Campaign, CampaignReport, Channel, Customer, MessageStatus,
  RecipientRow, RewardGrant, Segment, SkipReason,
} from './types';
import { SKIP_REASONS } from './types';
import { hashSeed, knownMockOutletIds, mulberry32 } from './mockReference';
import { getTrigger } from './mockAutomations';
import { consentStatus, isReachable } from '../../../redux/customers/customerConsent';
import { evaluateSegment, isBlocked, type CampaignHistory } from '../../../redux/customers/customerSegmentEngine';
import { analyzeSms } from '../utils/sms';
import { hasReport } from '../utils/campaignStatus';
import { smsCostCents } from '../utils/returns';

const DAY_MS = 86_400_000;
/** Mock money value of loyalty rewards; the live program defines its own value per point. */
const MOCK_POINT_VALUE_CENTS = 1;
const MOCK_FREE_REWARD_VALUE_CENTS = 300;
const MOCK_POINTS_PER_ORDER = 28;

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // OFR-03 — no 0, O, 1, I

export interface MockRunInput {
  campaign: Campaign;
  customers: Customer[];
  segments: Segment[];
  /** Who received and redeemed earlier campaigns, for rules that look at campaign history. */
  history?: CampaignHistory;
  brandName: string;
  /** Price of one SMS credit, for the SMS cost. */
  smsCreditPriceCents: number;
  now: Date;
}

export interface MockRun {
  recipients: RecipientRow[];
  report: CampaignReport;
  grants: RewardGrant[];
}

export const hasRun = hasReport;

function audienceFor(input: MockRunInput): Customer[] {
  const { campaign, customers, segments, history, now } = input;
  const ctx = { now, history };
  const rules = campaign.kind === 'automation'
    ? getTrigger(campaign.triggerType)?.mockAudienceRules
    : segments.find((s) => s.id === campaign.segmentId)?.rules;
  return rules ? evaluateSegment(rules, customers, ctx, knownMockOutletIds(campaign.outletIds)) : [];
}

function messageStatus(channel: Channel, r: number): MessageStatus {
  if (r < 0.03) return channel === 'email' ? 'bounced' : 'failed';
  if (r < 0.04) return 'opted_out';
  if (channel === 'sms') return r < 0.14 ? 'clicked' : 'delivered';
  if (channel === 'email') return r < 0.13 ? 'clicked' : r < 0.42 ? 'opened' : 'delivered';
  return r < 0.1 ? 'clicked' : r < 0.27 ? 'opened' : 'delivered';
}

function rewardLabel(campaign: Campaign): string {
  const cfg = campaign.rewardConfig;
  switch (campaign.rewardType) {
    case 'offer': return cfg.offerName ?? 'Offer';
    case 'bonus_points': return `${cfg.points ?? 0} bonus points`;
    case 'points_multiplier': return `${cfg.multiplier ?? 2}x points`;
    case 'free_reward': return cfg.loyaltyRewardName ?? 'Free reward';
    default: return '';
  }
}

export function buildMockRun(input: MockRunInput): MockRun {
  const { campaign, brandName, smsCreditPriceCents, now } = input;
  const rand = mulberry32(hashSeed(campaign.id));
  const audience = audienceFor(input);
  const sentAt = campaign.lastSentAt ?? new Date(now.getTime() - 3 * DAY_MS).toISOString();
  const runId = `run_${campaign.id}`;
  const smsSegments = analyzeSms(campaign.content.sms?.body ?? '', brandName).segments;
  const inProgress = campaign.status === 'sending' || (campaign.status === 'paused' && campaign.kind === 'one_time');
  const validDays = campaign.rewardConfig.validDays ?? 14;
  const loyaltyReward = campaign.rewardType !== 'none' && campaign.rewardType !== 'offer';

  const recipients: RecipientRow[] = [];
  const grants: RewardGrant[] = [];
  let seq = 0;

  const row = (c: Customer, overrides: Partial<RecipientRow>): RecipientRow => {
    seq += 1;
    return {
      id: `rcp_${campaign.id}_${seq}`, brandId: campaign.brandId, createdAt: sentAt, updatedAt: sentAt,
      campaignId: campaign.id, customerId: c.id, customerName: c.name, runId,
      isHoldout: false, status: 'selected', skipReason: null, channel: null, messageStatus: null,
      smsSegments: null, rewardGrantId: null, rewardCode: null, rewardStatus: null, ...overrides,
    };
  };

  const grantFor = (c: Customer): string | null => {
    if (campaign.rewardType === 'none') return null;
    // LOY-06 — non-members never receive a loyalty reward
    if (loyaltyReward && !c.loyaltyStatus) return null;
    const expiresAt = new Date(new Date(sentAt).getTime() + validDays * DAY_MS).toISOString();
    const redeemed = rand() < 0.11;
    const id = `grt_${campaign.id}_${c.id}`;
    grants.push({
      id, brandId: campaign.brandId, createdAt: sentAt, updatedAt: sentAt,
      campaignId: campaign.id, campaignName: campaign.name, customerId: c.id, type: campaign.rewardType,
      offerId: campaign.rewardConfig.offerId ?? null,
      code: campaign.rewardConfig.delivery === 'unique_code'
        ? Array.from({ length: 8 }, () => CODE_ALPHABET[Math.floor(rand() * CODE_ALPHABET.length)]).join('')
        : null,
      label: rewardLabel(campaign),
      expiresAt,
      status: redeemed ? 'redeemed' : expiresAt < now.toISOString() ? 'expired' : 'active',
      redeemedOrderId: redeemed ? `ord_${c.id}_r` : null,
    });
    return id;
  };

  for (const c of audience) {
    // A blocked customer matches the segment but gets no message and no reward.
    if (isBlocked(c)) {
      recipients.push(row(c, { status: 'skipped', skipReason: 'blocked' }));
      continue;
    }
    // CMP-10 — the holdout share is chosen at random; holdouts get no message and no reward
    if (campaign.holdoutPercent > 0 && rand() < campaign.holdoutPercent / 100) {
      recipients.push(row(c, { isHoldout: true, status: 'skipped', skipReason: 'holdout' }));
      continue;
    }
    const eligible = campaign.channels.filter((ch) => isReachable(c, ch));
    if (eligible.length === 0) {
      // CHN-13 — the skip reason of the highest-priority channel
      const first = campaign.channels[0];
      const reason: SkipReason = first && consentStatus(c, first) === 'granted' ? 'no_address' : 'no_consent';
      recipients.push(row(c, { status: 'skipped', skipReason: reason, channel: first ?? null }));
      continue;
    }
    const r = rand();
    if (r < 0.05) {
      // CHN-14 — a frequency-capped recipient still gets the reward
      recipients.push(row(c, { status: 'skipped', skipReason: 'frequency_cap', channel: eligible[0], rewardGrantId: grantFor(c) }));
      continue;
    }
    if (r < 0.07) {
      recipients.push(row(c, { status: 'skipped', skipReason: 'bad_address', channel: eligible[0] }));
      continue;
    }
    if (r < 0.08 && eligible[0] !== 'email') {
      recipients.push(row(c, { status: 'skipped', skipReason: 'quiet_hours_expired', channel: eligible[0] }));
      continue;
    }
    const grantId = grantFor(c);
    // CHN-11 / CHN-12
    const sendOn = campaign.channelMode === 'first_available' ? eligible.slice(0, 1) : eligible;
    for (const channel of sendOn) {
      const pending = inProgress && rand() < 0.45;
      recipients.push(row(c, {
        status: pending ? 'selected' : 'sent',
        channel,
        messageStatus: pending ? 'queued' : messageStatus(channel, rand()),
        smsSegments: channel === 'sms' ? smsSegments : null,
        rewardGrantId: grantId,
      }));
    }
  }

  // Show each recipient's code and what became of it.
  const grantById = new Map(grants.map((g) => [g.id, g]));
  for (const r of recipients) {
    const grant = r.rewardGrantId ? grantById.get(r.rewardGrantId) : undefined;
    if (grant?.code) { r.rewardCode = grant.code; r.rewardStatus = grant.status; }
  }

  return { recipients, grants, report: buildReport(campaign, recipients, grants, smsCreditPriceCents, now, rand) };
}

/** Money value of the loyalty rewards a campaign gave out. */
function loyaltyRewardCostCents(campaign: Campaign, grants: RewardGrant[], redemptions: number): number {
  switch (campaign.rewardType) {
    // Points are credited to every recipient when the campaign sends (LOY-02).
    case 'bonus_points': return grants.length * (campaign.rewardConfig.points ?? 0) * MOCK_POINT_VALUE_CENTS;
    // Only orders placed under the multiplier earn the extra points (LOY-03).
    case 'points_multiplier': return redemptions * ((campaign.rewardConfig.multiplier ?? 2) - 1) * MOCK_POINTS_PER_ORDER * MOCK_POINT_VALUE_CENTS;
    case 'free_reward': return redemptions * MOCK_FREE_REWARD_VALUE_CENTS;
    default: return 0;
  }
}

function buildReport(campaign: Campaign, recipients: RecipientRow[], grants: RewardGrant[], smsCreditPriceCents: number, now: Date, rand: () => number): CampaignReport {
  const perChannel = { sms: empty(), email: empty(), push: empty() };
  const skipped = Object.fromEntries(SKIP_REASONS.map((r) => [r, 0])) as Record<SkipReason, number>;
  const audienceIds = new Set<string>();
  const sentIds = new Set<string>();
  const clickIds = new Set<string>();
  let optedOut = 0;
  let smsCredits = 0;
  let holdoutSize = 0;

  for (const r of recipients) {
    if (r.isHoldout) holdoutSize += 1;
    else audienceIds.add(r.customerId);
    if (r.status === 'skipped' && r.skipReason) skipped[r.skipReason] += 1;
    if (r.status !== 'sent' || !r.channel || !r.messageStatus) continue;
    const funnel = perChannel[r.channel];
    funnel.sent += 1;
    sentIds.add(r.customerId);
    smsCredits += r.smsSegments ?? 0;
    const s = r.messageStatus;
    if (s === 'delivered' || s === 'opened' || s === 'clicked' || s === 'opted_out') funnel.delivered += 1;
    // RPT-05 — opens are not available for SMS
    if (r.channel !== 'sms' && (s === 'opened' || s === 'clicked')) funnel.opened += 1;
    if (s === 'clicked') { funnel.clicked += 1; clickIds.add(r.customerId); }
    if (s === 'opted_out') optedOut += 1;
  }

  const redemptions = grants.filter((g) => g.status === 'redeemed').length;
  const attributedOrders: Record<AttributionMethod, number> = {
    redemption: redemptions,
    click: Math.round(clickIds.size * 0.35),
    window: Math.round(sentIds.size * 0.07),
  };
  const orders = attributedOrders.redemption + attributedOrders.click + attributedOrders.window;
  const avgOrderCents = 2600 + Math.floor(rand() * 900);
  const recipientOrderRate = sentIds.size > 0 ? orders / sentIds.size : 0;
  const discountCents = campaign.rewardType === 'offer' ? redemptions * 420 : 0;
  const smsCost = smsCostCents(smsCredits, smsCreditPriceCents);
  const rewardCostCents = loyaltyRewardCostCents(campaign, grants, redemptions);

  return {
    id: `rpt_${campaign.id}`,
    campaignId: campaign.id,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
    audience: audienceIds.size,
    perChannel,
    skipped,
    uniqueClicks: clickIds.size,
    optedOut,
    redemptions,
    unredeemedGrants: grants.filter((g) => g.status === 'active').length,
    attributedOrders,
    revenueCents: orders * avgOrderCents,
    discountCents,
    smsCredits,
    smsCostCents: smsCost,
    rewardCostCents,
    spendCents: smsCost + discountCents + rewardCostCents,
    holdout: holdoutSize > 0
      ? { size: holdoutSize, holdoutOrderRate: Math.max(0, recipientOrderRate - 0.04), recipientOrderRate }
      : null,
  };
}

function empty() {
  return { sent: 0, delivered: 0, opened: 0, clicked: 0 };
}

export function totalSent(report: CampaignReport): number {
  return report.perChannel.sms.sent + report.perChannel.email.sent + report.perChannel.push.sent;
}
